import React from 'react';
import PropTypes from 'prop-types';
import Favicon from '../Favicon/Favicon';
import { useTheme } from '@mui/material';

/**
 * ArticleInfo - Componente atômico para exibir informações do artigo (fonte, autor, data)
 * Agora usa o átomo Favicon para exibir o favicon do site.
 */
const ArticleInfo = ({
  source,
  author,
  date,
  favicon,
  mediaType,
  variant = 'default',
  className = '',
  hideDate = false,
  onSourceClick,
  faviconSize = 16,
  sourceWeight = 500,
  sourceColor,
  faviconGap = 6,
  showFavicon = true,
  ...props
}) => {
  const theme = useTheme();
  const socialMediaTypes = ['YouTube', 'Twitter', 'Instagram', 'TikTok', 'Facebook', 'LinkedIn', 'Pinterest', 'Threads', 'Kwai'];
  const isSocialMedia = socialMediaTypes.includes(mediaType);
  const styles = {
    container: {
      display: 'flex',
      gap: '12px',
      fontSize: 'inherit',
      color: theme.palette.text.secondary,
      ...(variant === 'minimal' && { flexDirection: 'column', gap: '4px' })
    },
    source: {
      fontWeight: sourceWeight,
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      flex: 1,
      overflow: 'hidden',
      color: sourceColor || theme.palette.text.secondary,
      cursor: onSourceClick ? 'pointer' : 'default',
      '&:hover': onSourceClick ? {
        color: theme.palette.primary.main,
        textDecoration: 'underline'
      } : {}
    },
    author: {
      display: 'flex',
      alignItems: 'center',
      '&::before': {
        content: '"•"',
        marginRight: '4px'
      }
    },
    date: {
      display: 'flex',
      alignItems: 'center',
      '&::before': {
        content: '"•"',
        marginRight: '4px'
      }
    }
  };

  // Função para formatar a data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC'
      });
    } catch (error) {
      return dateString; // Fallback caso a data seja inválida
    }
  };

  return (
    <div
      className={`article-info article-info--${variant} ${className}`}
      style={styles.container}
      {...props}
    >
      {source && (
        <span
          className="article-info__source"
          style={styles.source}
          onClick={onSourceClick}
        >
          {showFavicon && favicon && (
            <Favicon
              url={favicon}
              mediaType={mediaType}
              size={faviconSize}
              imageScale={isSocialMedia ? 1.12 : 1}
              style={{ marginRight: faviconGap, flexShrink: 0 }}
            />
          )}
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0
          }}>
            {source}
          </span>
        </span>
      )}
      {date && !hideDate && (
        <span className="article-info__date" style={{
          ...styles.date,
          '&::before': variant === 'minimal' ? {} : styles.date['&::before']
        }}>
          {formatDate(date)}
        </span>
      )}
    </div>
  );
};

ArticleInfo.propTypes = {
  source: PropTypes.string,
  author: PropTypes.string,
  date: PropTypes.string,
  favicon: PropTypes.string,
  mediaType: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'minimal']),
  className: PropTypes.string,
  hideDate: PropTypes.bool,
  onSourceClick: PropTypes.func,
  faviconSize: PropTypes.number,
  sourceWeight: PropTypes.number,
  sourceColor: PropTypes.string,
  faviconGap: PropTypes.number,
  showFavicon: PropTypes.bool
};

export default ArticleInfo;
